import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { NavigationContext } from '../../Navigation/NavigationProvider';
import { useOrganization } from '../../Settings/SettingsDropDownMenu';
import { fetchService } from '../../../utils/fetchService';
import { GuardianReportHeaderAndMetadata } from './GuardianReportHeaderAndMetadata';
import PolicyViolations from './PolicyViolations/PolicyViolations';
import { ActionsButtonHeader } from './ActionsButtonHeader';
import { RootTabs } from './tabs/RootTabs';
import { transformRuleSummaries } from './utils/dopplerUtil';
import { useFetchRuleSummaries } from './utils/useFetchRuleSummaries';
import { PdfViewOptionsModal } from './PdfReport/PdfViewOptionsModal';

const fetcher = accessToken => path => fetchService(path, accessToken);

export function SarifReportContent({ analysisId, accessToken, backQParams }) {
  const {
    data: { appDetailsResponse, aggregateData, sbomData, threatScoreData },
    mutate,
  } = useSWR(
    [
      `/eval/applications/appDetails/${analysisId}`,
      `/eval/results/${analysisId}/aggregate-data`,
      `/eval/results/${analysisId}/sbom`,
      `/eval/results/${analysisId}/threat-score`,
    ],
    fetchDataInParallel(accessToken),
    {
      suspense: true,
      revalidateIfStale: true,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const { ruleSummariesData } = useFetchRuleSummaries(analysisId, {
    suspense: true,
    revalidateIfStale: true,
    revalidateOnMount: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { appDetails, appAnalysis } = appDetailsResponse;
  // Fetch App Details
  const { appName, appId, version, os } = appDetails;
  const iconUrl = appDetails.appIconBase64 ? `data:image/png;base64,${appDetails.appIconBase64}` : '';
  const triggeredPolicies = appAnalysis?.orgPolicyResults?.filter((p: any) => p.didTrigger) || [];
  const hasPolicyViolation = triggeredPolicies.length > 0;

  const { fullRuleSummaries, nonBehavioralCategoriesCount, behavioralCategoriesCount } = useMemo(
    () => transformRuleSummaries(ruleSummariesData),
    [ruleSummariesData],
  );

  const malwareRule = fullRuleSummaries?.find(ruleSummary => ruleSummary.rule?.id?.startsWith('malware/'));

  const { data: maliciousData } = useSWRImmutable(
    malwareRule?.guid ? `/eval/results/${analysisId}/evidence?ruleGuid=${malwareRule?.guid}` : null,
    fetcher(accessToken),
  );
  const maliciousResult = maliciousData?.entries && maliciousData.entries.length > 0 ? maliciousData.entries[0].result : {};

  const { setAppName } = useContext(NavigationContext);

  const { orgData } = useOrganization(accessToken);
  const showDeniedApps = orgData?.settings?.includes('denied-apps');

  const isDeniedApp = showDeniedApps && appAnalysis.isDenied;
  const isTrustedApp = appAnalysis.isAllowed;

  const { state } = useLocation();
  const pdfOptionsOpen = Boolean(state?.pdfOptionsOpen);

  const [isPdfOptionsOpen, setIsPdfOptionsOpen] = useState(false);

  const nonBehavioralRuleCategoryOptions = [
    { value: 'all', label: 'All' },
    ...Object.entries(nonBehavioralCategoriesCount).map(([category, count]) => ({
      label: `${category} (${count})`,
      value: category,
    })),
  ];
  const behavioralRuleCategoryOptions = [
    { value: 'all', label: 'All' },
    ...Object.entries(behavioralCategoriesCount).map(([category, count]) => ({
      label: `${category} (${count})`,
      value: category,
    })),
  ];

  useEffect(() => {
    setAppName(appName);
    setIsPdfOptionsOpen(pdfOptionsOpen);
  }, [appName, pdfOptionsOpen]);

  let tag: { label: string; color: string; iconSrc: string } | undefined;

  if (isDeniedApp) {
    tag = { label: 'Denied App', color: '#FF0000', iconSrc: '/icons/shield-x-red.svg' };
  } else if (isTrustedApp) {
    tag = { label: 'Trusted App', color: '#00B300', iconSrc: '/icons/shield-checked-green.svg' };
  } else if (hasPolicyViolation) {
    tag = { label: 'Policy Violation', color: '#FF0000', iconSrc: '/icons/alert-triangle-red.svg' };
  }

  return (
    <>
      <PdfViewOptionsModal
        isOpen={isPdfOptionsOpen}
        toggle={() => setIsPdfOptionsOpen(!isPdfOptionsOpen)}
        appName={appName}
        analysisId={analysisId}
        overviewRuleCategoryOptions={nonBehavioralRuleCategoryOptions}
        behavioralRuleCategoryOptions={behavioralRuleCategoryOptions}
        os={os}
      />
      <ActionsButtonHeader
        appAnalysis={appAnalysis}
        appDetails={appDetails}
        fetchDeniedApps={mutate}
        fetchTrustedApps={mutate}
        showDeniedApps={showDeniedApps}
        backQParams={backQParams}
        setPdfOptionsOpen={setIsPdfOptionsOpen}
      />
      <GuardianReportHeaderAndMetadata
        appName={appName}
        appId={appId}
        appVersion={version}
        os={os}
        iconUrl={iconUrl}
        appDetails={appDetails}
        appAnalysis={appAnalysis}
        tag={tag}
        threatScore={threatScoreData?.threatScore}
      />
      <PolicyViolations orgPolicyResults={appAnalysis?.orgPolicyResults} riskIndicators={appDetails?.iors} />

      <RootTabs
        aggregateData={aggregateData}
        ruleSummaries={fullRuleSummaries}
        analysisId={analysisId}
        maliciousResult={maliciousResult}
        os={os}
        appDetails={appDetails}
        sbomData={sbomData}
        accessToken={accessToken}
      />
    </>
  );
}

function fetchDataInParallel(accessToken: string) {
  return async (url0, url1, url2, url3) => {
    const [appDetailsResponse, aggregateData, sbomData, threatScoreData] = await Promise.all([
      fetchService(url0, accessToken),
      fetchService(url1, accessToken),
      fetchService(url2, accessToken),
      fetchService(url3, accessToken),
    ]);
    return {
      appDetailsResponse,
      aggregateData,
      sbomData,
      threatScoreData,
    };
  };
}
